// Single source of truth for Sectors → Services.
// Content sourced directly from the Zone-X Sectors & Services brief. Used by
// Nav.jsx (merged Sectors/Services menu), the sector pages, and
// ServiceDetailPage.jsx (one page per service, nested under its sector).

export const SECTORS = [
  {
    slug: 'healthcare',
    name: 'Healthcare',
    to: '/industries/healthcare',
    color: '#C0392B',
    heroImg: '/visuals/industries/Industry 1.png',
    deliveryNote: "Every Zone-X healthcare deployment is engineered with clinical reliability and data security as first-class requirements — not settings you configure later.",
    services: [
      {
        slug: 'neonatal-care-solutions',
        title: 'Neonatal Care Solutions',
        icon: 'thermometer',
        img: '/visuals/sector-services/healthcare/1.png',
        brief: "Every second matters in the NICU. We design intelligent neonatal care ecosystems that wrap fragile newborns in continuous, data-driven safety — from warming beds to vital sign integration — so clinicians can focus on healing, not hunting for data.",
        process: "We begin by walking your neonatal unit to understand bedside workflows. Then we integrate sensors, monitors, and alert systems into one gentle, non-intrusive network. Finally, we train your nursing staff until the technology feels like a natural extension of their care.",
        deliver: [
          'Real-time vital monitoring with automated early-warning alerts',
          'Integrated warming, phototherapy, and respiratory support telemetry',
          'NICU-grade dashboards for centralized patient visibility',
          '24/7 remote access for senior pediatricians and on-call teams',
        ],
      },
      {
        slug: 'phototherapy-treatment-systems',
        title: 'Phototherapy Treatment Systems',
        icon: 'sun',
        img: '/visuals/sector-services/healthcare/2.png',
        brief: "Jaundice in newborns is common — but managing its treatment shouldn't be guesswork. Our smart phototherapy systems track irradiance, treatment duration, and baby positioning automatically, ensuring every minute of blue-light therapy counts toward a safe, speedy recovery.",
        process: "We assess your current phototherapy workflow and replace manual logging with sensor-backed intelligence. Our team calibrates each unit to your hospital's protocols, then connects every device to a central dashboard for seamless audit and control.",
        deliver: [
          'LED phototherapy units with real-time irradiance feedback',
          'Automated treatment timers and skin-distance sensors',
          'Centralized phototherapy dashboards for nursing stations',
          'Digital logs for NABH compliance and quality audits',
        ],
      },
      {
        slug: 'remote-patient-monitoring',
        title: 'Remote Patient Monitoring (RPM)',
        icon: 'monitor',
        img: '/visuals/sector-services/healthcare/3.png',
        brief: "Healthcare doesn't end at the hospital door. Our remote patient monitoring solutions keep doctors connected to patients at home — tracking vitals, spotting trends, and flagging risks before they become emergencies. It's telehealth, made truly intelligent.",
        process: "We match the right wearable and home monitoring devices to your patient population. Our team handles device setup, patient onboarding, and EHR integration. Once live, our analytics engine turns daily vitals into actionable clinical insights.",
        deliver: [
          'Bluetooth-enabled BP, SpO₂, ECG, and glucometer integrations',
          'Patient-friendly mobile apps with medication reminders',
          'Clinician dashboards with trend analysis and anomaly alerts',
          'Seamless HL7/FHIR integration with your existing hospital EMR',
        ],
      },
      {
        slug: 'nutrition-assessment-digital-health',
        title: 'Nutrition Assessment & Digital Health Platforms',
        icon: 'activity',
        img: '/visuals/sector-services/healthcare/4.png',
        brief: "Good care starts with good data — especially when it comes to what patients eat. Our digital health platforms combine AI-powered nutrition assessment with personalized diet planning, helping dietitians and doctors turn meal charts into recovery fuel.",
        process: "We study your existing dietetics workflow, then deploy our cloud-based assessment tools. Our platform captures patient dietary intake, analyzes macro and micronutrient gaps, and generates diet plans aligned with treatment goals — all in minutes, not hours.",
        deliver: [
          'AI-driven nutrition analysis from food diaries and images',
          'Personalized diet plans for diabetes, renal, cardiac, and pediatric care',
          'Integration with hospital EMR and billing systems',
          'Patient engagement portals with progress tracking',
        ],
      },
      {
        slug: 'connected-medical-devices',
        title: 'Connected Medical Devices (Medical IoT)',
        icon: 'plug',
        img: '/visuals/sector-services/healthcare/5.png',
        brief: "Your hospital is full of brilliant machines — but most of them can't talk to each other. Our Medical IoT platform connects ventilators, infusion pumps, patient monitors, and more into one intelligent network, unlocking real-time visibility across your entire care ecosystem.",
        process: "We audit your device landscape, identify connectivity gaps, and deploy secure IoT gateways. Using IoMT (Internet of Medical Things) protocols, we unify data streams into a single pane of glass — without disrupting your existing IT infrastructure.",
        deliver: [
          'Secure medical device integration and interoperability',
          'Real-time asset tracking and utilization analytics',
          'Predictive alerts for device maintenance and calibration',
          'FDA and HIPAA-compliant data pipelines',
        ],
      },
    ],
  },
  {
    slug: 'utilities-energy',
    name: 'Utilities & Energy',
    to: '/industries/utilities-energy',
    color: '#E67E22',
    heroImg: '/visuals/industries/Industry 4.png',
    deliveryNote: "Every Zone-X utilities deployment is built to survive real field conditions — dust, heat, and unreliable power — without needing a technician on-site to keep it running.",
    services: [
      {
        slug: 'energy-monitoring-analytics',
        title: 'Energy Monitoring & Analytics',
        icon: 'bar',
        img: '/visuals/sector-services/utilities-energy/1.png',
        brief: "You can't manage what you can't measure. Our energy monitoring and analytics platform turns your facility's power consumption into clear, visual stories — showing exactly where energy goes, when it peaks, and how to tame it.",
        process: "We install smart meters and IoT sensors across your electrical distribution network. Data flows to our cloud analytics engine, where AI identifies waste patterns, peak load anomalies, and savings opportunities. We then hand you a roadmap to lower bills and greener operations.",
        deliver: [
          'Real-time energy dashboards with sub-metering granularity',
          'Power quality analysis and demand forecasting',
          'Automated alerts for abnormal consumption patterns',
          'Sustainability reporting aligned with ISO 50001 and LEED',
        ],
      },
      {
        slug: 'battery-energy-storage-monitoring',
        title: 'Battery & Energy Storage Monitoring',
        icon: 'zap',
        img: '/visuals/sector-services/utilities-energy/2.png',
        brief: "Batteries are the silent heroes of your backup power — until they fail without warning. Our energy storage monitoring solution watches every cell, temperature fluctuation, and charge cycle, so your UPS and battery banks are always ready when the grid goes down.",
        process: "We deploy battery monitoring sensors across your storage infrastructure. Our platform tracks state-of-charge, state-of-health, and thermal behavior in real time. Predictive algorithms then forecast replacement needs before failure strikes.",
        deliver: [
          'Cell-level battery health monitoring and trending',
          'Thermal runaway early detection and alerts',
          'Automated charge/discharge cycle optimization',
          'Integration with BMS (Battery Management Systems) and SCADA',
        ],
      },
      {
        slug: 'dg-automation-remote-control',
        title: 'DG Automation & Remote Control',
        icon: 'automation',
        img: '/visuals/sector-services/utilities-energy/3.png',
        brief: "Your diesel generator is your last line of defense during power cuts — but are you sure it will start? Our DG automation and remote control systems let you start, stop, monitor, and diagnose your generators from anywhere, ensuring 100% readiness without sending a technician to the plant room.",
        process: "We retrofit your existing DG sets with IoT-enabled auto-start controllers, fuel sensors, and remote telemetry units. Our platform connects every generator to a centralized command center, giving you full remote control plus automated scheduling and load management.",
        deliver: [
          'Remote start/stop and auto-transfer switch control',
          'Real-time fuel level, run-hour, and battery status monitoring',
          'Automated AMF (Auto Mains Failure) logic and load shedding',
          'Maintenance alerts based on actual engine health, not just calendars',
        ],
      },
      {
        slug: 'renewable-energy-solar-monitoring',
        title: 'Renewable Energy & Solar Monitoring',
        icon: 'sun',
        img: '/visuals/sector-services/utilities-energy/4.png',
        brief: "The sun gives you free power — but only if your solar plant is performing at its peak. Our renewable energy monitoring platform tracks every panel, inverter, and string in real time, turning your rooftop or ground-mount solar farm into a transparent, high-yield asset.",
        process: "We install IoT sensors and string monitoring units across your PV array. Data is aggregated into a cloud dashboard that shows generation trends, shading losses, and inverter efficiency. Our analytics pinpoint underperforming strings so your O&M team fixes issues fast.",
        deliver: [
          'Panel-level and string-level performance monitoring',
          'Irradiance vs. generation comparison and PR (Performance Ratio) tracking',
          'Inverter fault detection and remote diagnostics',
          'Automated generation reports for investors and regulators',
        ],
      },
      {
        slug: 'industrial-electrical-asset-monitoring',
        title: 'Industrial Electrical Asset Monitoring',
        icon: 'plug',
        img: '/visuals/sector-services/utilities-energy/5.png',
        brief: "Transformers, switchgear, and MCC panels are the backbone of your plant — but they rarely tell you when they're hurting. Our industrial electrical asset monitoring solution listens to their electrical heartbeat 24/7, catching overheating, overloads, and insulation degradation before they trigger a shutdown.",
        process: "We install thermal, partial discharge, and current sensors on critical electrical assets. Our edge devices stream data to an analytics platform that learns normal operating signatures. Deviations trigger immediate alerts, letting your maintenance team act while the problem is still small.",
        deliver: [
          'Transformer and switchgear thermal and partial discharge monitoring',
          'Real-time load profiling and harmonic analysis',
          'Predictive maintenance alerts for electrical failures',
          'Integration with existing CMMS and ERP systems',
        ],
      },
      {
        slug: 'predictive-maintenance-industrial-iot',
        title: 'Predictive Maintenance & Industrial IoT',
        icon: 'tool',
        img: '/visuals/sector-services/utilities-energy/6.png',
        brief: "Why fix machines after they break when you can fix them before? Our predictive maintenance platform uses Industrial IoT sensors and machine learning to turn vibration, temperature, and current data into early warnings — shifting your plant from reactive firefighting to proactive precision.",
        process: "We instrument your critical rotating equipment with wireless vibration and temperature sensors. Our AI models learn each machine's unique “fingerprint,” then continuously compare live data to baseline health. When something drifts, you get a clear alert with recommended action.",
        deliver: [
          'Wireless vibration, temperature, and current sensing',
          'AI-driven fault prediction (bearing wear, misalignment, imbalance)',
          'Maintenance scheduling based on actual asset condition',
          'OEE (Overall Equipment Effectiveness) improvement dashboards',
        ],
      },
    ],
  },
  {
    slug: 'industrial-iot',
    name: 'Industrial IoT & Automation',
    to: '/industries/industrial-iot',
    color: '#5D4037',
    heroImg: '/visuals/industries/Industry 5.png',
    deliveryNote: "Every Zone-X industrial deployment retrofits onto your existing machinery — no OEM lock-in, and no forklift upgrade required just to get started.",
    services: [
      {
        slug: 'smart-factory-machine-monitoring',
        title: 'Smart Factory & Machine Monitoring',
        icon: 'gauge',
        img: '/visuals/sector-services/industrial-iot/1.png',
        brief: "A smart factory doesn't just make things — it knows exactly how it's making them, every second of every shift. Our machine monitoring platform connects your CNCs, presses, and conveyors to give you live OEE, downtime reasons, and production counts without a single clipboard in sight.",
        process: "We tap into your machines via PLC, OPC-UA, or non-invasive sensors. Within days, every machine starts whispering its status to a central cloud dashboard. We configure shift-based reporting, alert thresholds, and operator interfaces so your team owns the data from day one.",
        deliver: [
          'Real-time machine status, cycle time, and OEE dashboards',
          'Automatic downtime classification and root-cause tracking',
          'Production count reconciliation with ERP/MES',
          'Operator-friendly Andon displays and mobile alerts',
        ],
      },
      {
        slug: 'predictive-maintenance-solutions',
        title: 'Predictive Maintenance Solutions',
        icon: 'tool',
        img: '/visuals/sector-services/industrial-iot/2.png',
        brief: "Your machines speak a language of vibration, heat, and sound — most factories just aren't listening. Our predictive maintenance solutions decode that language using Industrial IoT and AI, translating subtle changes into clear maintenance actions weeks before failure.",
        process: "We map your critical asset list and install the right mix of vibration, thermal, and oil-analysis sensors. Our cloud platform builds machine-specific baselines, then watches for anomalies. When a bearing whispers that it's tired, your team gets an alert with a work order — not an emergency call.",
        deliver: [
          'Multi-sensor condition monitoring (vibration, temperature, acoustics)',
          'Machine learning-based failure mode prediction',
          'Automated work order generation and spare parts forecasting',
          'Digital maintenance logs and compliance reporting',
        ],
      },
      {
        slug: 'industrial-data-acquisition-dashboards',
        title: 'Industrial Data Acquisition & Dashboards',
        icon: 'dashboard',
        img: '/visuals/sector-services/industrial-iot/3.png',
        brief: "Data is everywhere on your shop floor — trapped in PLCs, SCADA systems, paper logs, and operator memory. Our industrial data acquisition platform liberates that data, cleans it, and serves it on beautiful, real-time dashboards that help managers make decisions in minutes, not Mondays.",
        process: "We connect to your existing PLCs, sensors, and databases using industrial protocols (Modbus, OPC-UA, MQTT). Data is normalized in the cloud, then visualized in role-based dashboards. We train your team to build their own reports, so IT bottlenecks become a thing of the past.",
        deliver: [
          'Multi-protocol industrial data acquisition and edge computing',
          'Real-time operational dashboards for production, quality, and energy',
          'Historical trending and shift-wise performance comparisons',
          'Role-based access for operators, engineers, and plant heads',
        ],
      },
      {
        slug: 'connected-industrial-iot-platforms',
        title: 'Connected Industrial IoT Platforms',
        icon: 'plug',
        img: '/visuals/sector-services/industrial-iot/4.png',
        brief: "Industry 4.0 isn't about buying gadgets — it's about building a nervous system for your plant. Our connected Industrial IoT platform unites machines, meters, environment sensors, and people into one intelligent ecosystem that learns, adapts, and optimizes your operations continuously.",
        process: "We design your IoT architecture around your business goals — whether that's traceability, energy savings, or quality control. Then we deploy edge gateways, cloud infrastructure, and analytics layers. Finally, we integrate with your ERP and MES so data flows seamlessly across your digital thread.",
        deliver: [
          'End-to-end IoT platform architecture and deployment',
          'Edge-to-cloud data pipelines with enterprise security',
          'Digital twin readiness and asset modeling',
          'API integrations with SAP, Oracle, and custom MES systems',
        ],
      },
      {
        slug: 'industrial-asset-utility-monitoring',
        title: 'Industrial Asset & Utility Monitoring',
        icon: 'activity',
        img: '/visuals/sector-services/industrial-iot/5.png',
        brief: "Your plant runs on more than machines — it runs on compressed air, water, steam, and power. Our industrial asset and utility monitoring solution tracks every utility flow and critical asset in one view, exposing leaks, inefficiencies, and hidden costs that drain your margins silently.",
        process: "We install flow, pressure, and energy sensors across your utility distribution network. Our platform correlates utility consumption with production output, revealing true cost-per-unit. We then configure alerts for anomalies like air compressor leaks or cooling tower inefficiencies.",
        deliver: [
          'Compressed air, water, steam, and gas flow monitoring',
          'Utility cost-per-unit production tracking',
          'Leak detection and energy waste identification',
          'Environmental parameter monitoring (temperature, humidity, dust)',
        ],
      },
      {
        slug: 'industrial-automation-control-systems',
        title: 'Industrial Automation & Control Systems',
        icon: 'automation',
        img: '/visuals/sector-services/industrial-iot/1.png',
        brief: "Repetition breeds error — but automation breeds consistency. Our industrial automation and control systems replace manual intervention with precision PLC/SCADA logic, giving you tighter process control, safer operations, and the flexibility to scale without proportional hiring.",
        process: "We study your process flow, identify automation opportunities, and design control architectures using PLCs, HMIs, and SCADA. Our engineers program, test, and commission every loop on-site. We then train your operators and hand over complete documentation and source code.",
        deliver: [
          'PLC, HMI, and SCADA-based control system design',
          'Process automation for mixing, filling, packaging, and material handling',
          'Safety interlocks and alarm management',
          'Complete as-built documentation and operator training',
        ],
      },
    ],
  },
  {
    slug: 'education',
    name: 'Education',
    to: '/industries/education',
    color: '#1565A7',
    heroImg: '/visuals/industries/Industry 6.png',
    deliveryNote: "Every Zone-X education deployment is built for real classrooms and labs — durable hardware, teacher-friendly software, and curricula your faculty can actually run.",
    services: [
      {
        slug: 'stem-iot-learning-kits',
        title: 'STEM & IoT Learning Kits',
        icon: 'cpu',
        img: '/visuals/sector-services/education/1.png',
        brief: "The best engineers of tomorrow are the curious kids who build things today. Our STEM and IoT learning kits give students hands-on tools to explore circuits, sensors, coding, and connected devices — turning abstract textbook concepts into tangible, blinky, beeping reality.",
        process: "We curate age-appropriate kits aligned with your curriculum goals — from basic electronics for middle school to advanced IoT for higher secondary. Each kit comes with structured lesson plans, video tutorials, and teacher guides. We also conduct train-the-trainer workshops so educators lead with confidence.",
        deliver: [
          'Grade-specific STEM kits with sensors, microcontrollers, and actuators',
          'Curriculum-mapped lesson plans and activity manuals',
          'Coding environments (Scratch, Python, Blockly) pre-configured',
          'Teacher training and ongoing technical support',
        ],
      },
      {
        slug: 'robotics-automation-labs',
        title: 'Robotics & Automation Labs',
        icon: 'automation',
        img: '/visuals/sector-services/education/2.png',
        brief: "Robotics isn't just about building machines — it's about building problem-solvers. Our robotics and automation labs transform empty rooms into innovation hubs where students design, code, and compete with real robots, developing computational thinking and teamwork that classrooms alone can't teach.",
        process: "We assess your space, budget, and learning objectives, then design a complete lab layout — hardware, software, furniture, and safety. Our team installs the equipment, configures the programming environment, and trains your faculty. We also help you launch your first student robotics club or competition team.",
        deliver: [
          'Complete robotics lab setup with hardware, software, and furniture',
          'Programming platforms supporting Python, C++, and visual coding',
          'Curriculum integration for AI, machine learning, and automation concepts',
          'Competition-ready kits for FIRST, RoboCup, and national-level events',
        ],
      },
      {
        slug: 'engineering-lab-development',
        title: 'Engineering Lab Development',
        icon: 'tool',
        img: '/visuals/sector-services/education/3.png',
        brief: "Great engineers need great labs. We design and build engineering labs that bridge the gap between university theory and industry practice — complete with modern equipment, industry-standard software, and lab curricula that make graduates truly job-ready from day one.",
        process: "We collaborate with your faculty and industry advisory board to define learning outcomes. Then we source, install, and commission lab equipment across mechanical, electrical, electronics, and computer science domains. We finalize with faculty training, lab manuals, and accreditation support.",
        deliver: [
          'Turnkey engineering labs for mechanical, electrical, CS, and ECE departments',
          'Industry-standard software and hardware ecosystems',
          'Custom lab manuals aligned with AICTE, NBA, and NAAC norms',
          'Faculty development programs and periodic upgrades',
        ],
      },
      {
        slug: 'cloud-based-learning-platforms',
        title: 'Cloud-Based Learning Platforms',
        icon: 'cloud',
        img: '/visuals/sector-services/education/4.png',
        brief: "Learning shouldn't stop at the school gate. Our cloud-based learning platforms give students and teachers a digital campus accessible from any device — with live classes, interactive content, progress tracking, and AI-driven personalization that makes every learner feel seen.",
        process: "We deploy a secure, white-label learning management system on the cloud. We migrate your existing content, configure user roles, and integrate assessment engines. Our AI layer then personalizes learning paths based on student performance, while analytics give administrators real-time insight into engagement and outcomes.",
        deliver: [
          'White-label LMS with live video, recorded lectures, and digital library',
          'AI-powered adaptive learning paths and doubt resolution',
          'Automated assessments, proctoring, and gradebooks',
          'Parent portals and detailed learning analytics dashboards',
        ],
      },
    ],
  },
];

export function findSector(sectorSlug) {
  return SECTORS.find(s => s.slug === sectorSlug) || null;
}

export function findService(sectorSlug, serviceSlug) {
  const sector = findSector(sectorSlug);
  if (!sector) return null;
  const service = sector.services.find(s => s.slug === serviceSlug);
  if (!service) return null;
  return { sector, service };
}
